import { datadogRum } from '@datadog/browser-rum';
import axios from 'axios';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';

import type { Auth0ClientAdapter } from '@wetransfer/authentication.js';

import { client as graphQLClient } from '../../_graphql';
import { FINALIZE_SSO_URL_PARAM, SSO_PERSIST_TIER } from '../constants';
import * as auth0Helper from '../helpers/auth0';
import { returnedFromSSO } from '../helpers/sso';
import { State as AuthState, AppState } from '../types';
import API from '@wetransfer/transfer/api';
import {
  DD_LOGIN_REQUIRED,
  DD_USER_UNKNOWN,
} from '@wetransfer/transfer/constant/datadog';
import { PLAN_TIER } from '@wetransfer/transfer/constant/plan-tier';
import ModelHelper from '@wetransfer/transfer/helper/model';
import { userHasPaidTier } from '@wetransfer/transfer/helper/user';
import { removeUserId } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import ErrorHandler from '@wetransfer/transfer/modules/error-handler';
import Logger from '@wetransfer/transfer/modules/logger';
import accountActions from '@wetransfer/transfer/store/action/account';
import userActions from '@wetransfer/transfer/store/action/user';
import { State } from '@wetransfer/transfer/store/types';

interface AuthError extends Error {
  error?: 'login_required' | 'user_unknown' | 'do_login';
}

// Action types
export enum AUTH_ACTION_TYPES {
  UPDATE_STATUS = 'AUTH_UPDATE_STATUS',
  UPDATE_APP_STATE = 'AUTH_UPDATE_APP_STATE',
  UPDATE_SYNC = 'AUTH_UPDATE_SYNC',
  LOADING = 'AUTH_LOADING',
}

export interface UpdateStatusAction {
  type: AUTH_ACTION_TYPES.UPDATE_STATUS;
  isLoggedIn: AuthState['isLoggedIn'];
  givenName?: AuthState['givenName'];
  familyName?: AuthState['familyName'];
  email?: AuthState['email'];
  emailIsLocked?: AuthState['emailIsLocked'];
  picture?: AuthState['picture'];
  auth0UserId: AuthState['auth0UserId'];
  loginProvider?: AuthState['https://wetransfer.com/provider'];
}

export interface UpdateAppStateAction {
  type: AUTH_ACTION_TYPES.UPDATE_APP_STATE;
  state?: AppState;
}

export interface UpdateSyncAction {
  type: AUTH_ACTION_TYPES.UPDATE_SYNC;
  updates: Record<string, unknown>;
}

export interface LoadingAction {
  type: AUTH_ACTION_TYPES.LOADING;
  isLoading: AuthState['isLoading'];
}

// Action creators
export function createUpdateStatusAction(
  status: Partial<Omit<UpdateStatusAction, 'type'>>
): UpdateStatusAction {
  if (status.isLoggedIn !== true) {
    return {
      type: AUTH_ACTION_TYPES.UPDATE_STATUS,
      isLoggedIn: false,
      auth0UserId: '',
    };
  }

  return {
    type: AUTH_ACTION_TYPES.UPDATE_STATUS,
    isLoggedIn: false,
    auth0UserId: '',
    ...status,
  };
}

export function createUpdateAppStateAction(
  state?: AppState
): UpdateAppStateAction {
  return {
    type: AUTH_ACTION_TYPES.UPDATE_APP_STATE,
    state,
  };
}

export function createUpdateSyncAction(
  updates: UpdateSyncAction['updates']
): UpdateSyncAction {
  return {
    type: AUTH_ACTION_TYPES.UPDATE_SYNC,
    updates,
  };
}

export function createLoadingAction(
  isLoading?: LoadingAction['isLoading']
): LoadingAction {
  return {
    type: AUTH_ACTION_TYPES.LOADING,
    isLoading,
  };
}

// Actions
export function authenticate(): ThunkAction<void, State, null, Action> {
  return async (dispatch, getState) => {
    await dispatch(createLoadingAction(true));

    // Fetch the appState from the cache
    try {
      const signUpTier = localStorage.getItem(SSO_PERSIST_TIER) as
        | PLAN_TIER
        | null
        | undefined;

      if (Object.values(PLAN_TIER).includes(signUpTier)) {
        await dispatch(createUpdateAppStateAction({ signUpTier }));
      }
    } catch {
      // do nothing
    }

    const handleError = async (error: AuthError) => {
      if (error.error === 'login_required') {
        // update status: loggedOut
        await dispatch(createUpdateStatusAction({}));
        datadogRum.addAction(DD_LOGIN_REQUIRED);
      } else if (error.error === 'user_unknown') {
        /*
          This happens when a user is authenticated but not known to our
          backend, and not on the sign-up flow.

          cases:
          - Incomplete sign-up
          - User came from another one of our apps
        */
        datadogRum.addAction(DD_USER_UNKNOWN);
      } else if (error.error === 'do_login') {
        // Dispatch login action only when user is not in log-in page
        // Because log-in page will take care of logging in the user
        // Otherwise, we might endup calling login function twice and it would
        // cancel the ongoing authorize request which will eventually
        // logout the user instead of logging them in
        if (location.pathname !== '/log-in') {
          await dispatch(
            login({
              redirectUri: window.location.href,
            })
          );
        }
      } else {
        Logger.error(error);
        ErrorHandler.track(error);
      }
    };

    const auth0 = await auth0Helper.load();

    // Initialize auth, we handle the init error separately to prevent it from
    // blocking the flow
    try {
      // first time we land on the app after returning from SSO
      // https://auth0.com/docs/libraries/auth0-single-page-app-sdk#login-with-redirect-callback
      if (returnedFromSSO()) {
        const callbackData = await (
          auth0.getActiveAuthClient() as Auth0ClientAdapter
        ).handleRedirectCallback();
        const redirectUri = callbackData?.appState?.redirectUri;

        if (redirectUri) {
          window.location.href = redirectUri;

          return;
        }

        const signUpTier = callbackData?.appState?.tier as
          | PLAN_TIER
          | undefined;

        if (Object.values(PLAN_TIER).includes(signUpTier)) {
          // update app state
          await dispatch(createUpdateAppStateAction({ signUpTier }));

          // persist app state
          localStorage.setItem(SSO_PERSIST_TIER, signUpTier);
        }
      }
    } catch (error) {
      // ignore Invalid state errors, these are caused by users refreshing the
      // page during sign-up
      if (error.message !== 'Invalid state') {
        await handleError(error);
      }
    }

    try {
      // If refresh tokens feature is on and user verifies their email
      // then clear the cached tokens so that new tokens can be issued and
      // screen will be updated accordingly
      if (location.pathname === '/log-in') {
        auth0.clearLocalCache();
      }

      // get auth0 session
      await auth0.getTokenSilently();

      // get user data
      const authUser = await (
        auth0.getActiveAuthClient() as Auth0ClientAdapter
      ).getUser();

      // get token claims
      const tokenClaims = await auth0.getIdTokenClaims();

      // add token to global api handler
      axios.interceptors.request.use(async (config) => {
        // This methods adds the Auth header to all internal API requests,
        // unless stated otherwise in the constants, it also attaches the
        // id_token to the body of requests defined in the constants
        const newConfig = await auth0Helper.addAuthToApiRequestConfig({
          config,
          handleError,
          idToken: tokenClaims.__raw,
          graphQLClient,
        });

        // update status: loggedIn
        await dispatch(
          createUpdateStatusAction({
            isLoggedIn: true,
            givenName: authUser.given_name,
            familyName: authUser.family_name ?? '',
            email: authUser.email,
            picture: authUser.picture,
            auth0UserId: authUser.sub,
            // if the sub does not start with 'auth0|' it means that the user
            // has used a social identity provider, and we're currently not able
            // to change their email address on our end.
            emailIsLocked: !authUser.sub?.startsWith('auth0|') ?? false,
            loginProvider: authUser['https://wetransfer.com/provider'],
          })
        );

        return newConfig;
      });

      // fetch user
      const user = await dispatch(userActions.getUser());

      if ((user === undefined || user.id === false) && !returnedFromSSO()) {
        const unknownUserError: AuthError = new Error('Unknown user');
        unknownUserError.error = 'user_unknown';
        throw unknownUserError;
      }

      // fetch account
      const account = ModelHelper.getAccountFromUser(user);

      const domain = getState().session.domain;

      // Refresh cookie every time users authenticate. This happens on every
      // page load. It should be fine to refresh the cookie so often.
      userActions.setWTPlusCookie({ planTier: user.plan_tier, domain });

      // should redirect to the correct subdomain?
      if (
        // has subdomain?
        account?.subdomain_name !== undefined &&
        // account is active
        account?.active_subscription === true &&
        // user is Paid
        userHasPaidTier(user) &&
        // is currently on our main domain?
        location.hostname === domain
      ) {
        const path = location.pathname.replace('/account/callback', '/');
        // When returning from SSO we don't want to keep any query parameters.
        // But in all other cases, such as users clicking links in emails and
        // navigating to the app, and get redirected, after realising they are
        // a Pro user, we want to keep the query parameters. For example, in
        // case of an unsubscribe link, this might contain query parameters
        // that we want to keep and use them in subsequent requests to the API.
        const search = returnedFromSSO() ? '' : location.search;

        const channelUrl = new URL(
          `${location.protocol}//${account.subdomain_name}.${domain}${path}${search}`
        );

        if (location.port !== '') {
          channelUrl.port = location.port;
        }

        // end auth flow early
        return (location.href = channelUrl.href);
      }

      // check if the user has a valid account (made it passed sign-up)
      if (account?.id !== undefined) {
        // tell the back-end to check for user changes if we found some
        if (
          user.email.toLowerCase() !== authUser.email.toLowerCase() ||
          user.is_verified !== authUser.email_verified ||
          ((authUser.family_name || user.family_name) &&
            authUser.family_name !== user.family_name) ||
          user.given_name !== authUser.given_name
        ) {
          const changedDetails: {
            body?: Record<string, string>;
          } = await API.User.sync();

          // check if there are changes to the user
          if (Object.keys(changedDetails?.body || {}).length > 0) {
            // fetch user again, since it might be changed
            await dispatch(userActions.getUser());

            // emit updated values
            await dispatch(createUpdateSyncAction(changedDetails.body));
          }
        }

        await Promise.all([
          // fetch account details
          dispatch(accountActions.getAccount(account.id)),
          // fetch the subscription, because it might not exist when the
          // app loads, and we need this info to be available when free
          // accounts users end up in the app, and they want to upgrade.
          dispatch(
            accountActions.getSubscription(account.id, account.subscription.id)
          ),
        ]);

        // clear possible persisted data
        localStorage.removeItem(SSO_PERSIST_TIER);
      }
    } catch (error) {
      await handleError(error);
    }

    await dispatch(createLoadingAction(false));
  };
}

export function signUp(
  tier: PLAN_TIER,
  search?: string,
  params?: Record<string, unknown>
): ThunkAction<void, State, null, Action> {
  return async (dispatch, getState) => {
    await dispatch(createLoadingAction(true));

    const newSearch = new URLSearchParams(
      (search || location.search || '').replace('?', '')
    );
    newSearch.append(FINALIZE_SSO_URL_PARAM, '1');

    const lang = getState().user?.language || 'en';

    auth0Helper.signUp({
      tier,
      search: newSearch.toString(),
      lang,
      ...(params && { authPageParams: params }),
    });
  };
}

export function login(appState?: {
  [key: string]: unknown;
}): ThunkAction<void, State, null, Action> {
  return async (dispatch, getState) => {
    await dispatch(createLoadingAction(true));

    const newSearch = new URLSearchParams(
      (location.search || '').replace('?', '')
    );
    newSearch.append(FINALIZE_SSO_URL_PARAM, '1');
    newSearch.append('login', '1');

    const lang = getState().user?.language || 'en';

    auth0Helper.login({ search: newSearch.toString(), lang, appState });
  };
}

export function logout(): ThunkAction<void, State, null, Action> {
  return async (dispatch, getState) => {
    await dispatch(createLoadingAction(true));

    // update status: loggedOut
    await dispatch(createUpdateStatusAction({}));

    const domain = getState().session?.domain;

    await auth0Helper.logout(domain);

    userActions.unsetWTPlusCookie({ domain });
    removeUserId();
    datadogRum.removeUser();
  };
}

export function forgotPassword(): ThunkAction<
  Promise<void>,
  State,
  null,
  Action
> {
  return async (dispatch, getState) => {
    await dispatch(createLoadingAction(true));
    const lang = getState().user?.language || 'en';
    auth0Helper.forgotPassword({ lang });
    await dispatch(createLoadingAction(false));
  };
}

export function resetPassword(): ThunkAction<
  Promise<void>,
  State,
  null,
  Action
> {
  return async (dispatch) => {
    await dispatch(createLoadingAction(true));
    await API.User.triggerPasswordReset();
    await dispatch(createLoadingAction(false));
  };
}
